import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";

/**
 * Cron links API — simple scheduled GET hits managed from the Crons tab and
 * fired by the backend scheduler (spark cron:tick). Mirrors provisioningApi
 * conventions (Bearer token, APIError, POST endpoints).
 */

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

export interface CronJobDTO {
  id: number;
  name: string;
  url: string;
  schedule: string;
  timeout_s: number;
  enabled: number; // 1 | 0
  last_run_at: string | null;
  last_status: "success" | "failed" | null;
  last_http_code: number | null;
  last_duration_ms: number | null;
  last_error: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CronJobInput {
  name: string;
  url: string;
  schedule: string;
  timeout_s?: number;
  enabled?: boolean;
}

interface ListJobsResponseDTO {
  success: boolean;
  message: string;
  data: { jobs: CronJobDTO[] };
}

interface JobResponseDTO {
  success: boolean;
  message: string;
  data: { job: CronJobDTO };
}

interface RunJobResponseDTO {
  success: boolean;
  message: string;
  data: {
    job: CronJobDTO;
    result: {
      status: "success" | "failed" | "skipped";
      http_code: number;
      duration_ms: number;
      error: string | null;
    };
  };
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const token = getToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new APIError(
        payload.message || `HTTP error! status: ${response.status}`,
        response.status,
        payload
      );
    }

    return payload as T;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
}

export const listCronJobs = async (): Promise<ListJobsResponseDTO> =>
  postJson<ListJobsResponseDTO>("/api/crons/list", {});

export const createCronJob = async (input: CronJobInput): Promise<JobResponseDTO> =>
  postJson<JobResponseDTO>("/api/crons", input);

export const updateCronJob = async (
  id: number,
  updates: Partial<CronJobInput>
): Promise<JobResponseDTO> =>
  postJson<JobResponseDTO>("/api/crons/update", { id, ...updates });

export const deleteCronJob = async (id: number): Promise<{ success: boolean; message: string }> =>
  postJson("/api/crons/delete", { id });

export const runCronJob = async (id: number): Promise<RunJobResponseDTO> =>
  postJson<RunJobResponseDTO>("/api/crons/run", { id });

export { APIError as CronAPIError };
